/**
 * useStagingSession Hook
 *
 * Manages the staging session lifecycle for the V4 editor.
 * - Starts/resumes a staging session on mount
 * - Acquires the edit lock
 * - Provides saveElement() for persisting changes
 * - Heartbeat every 5 minutes to keep lock alive
 * - Releases lock on unmount
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  startStagingSession as defaultStartSession,
  saveStagingElement as defaultSaveElement,
  refreshStagingLock as defaultRefreshLock,
  releaseStagingLock as defaultReleaseLock,
  revertAllChanges as defaultRevertAll,
} from '../../api/editor';

// =============================================================================
// TYPES
// =============================================================================

export interface StagingSessionState {
  /** Whether the session is initializing */
  isStarting: boolean;
  /** Session ID (null if not started) */
  sessionId: string | null;
  /** Error during session management */
  error: string | null;
  /** Number of elements changed in this session */
  dirtyCount: number;
  /** Whether we hold the edit lock */
  hasLock: boolean;
  /** Map of pending changes: elementId → newValue */
  pendingChanges: Record<string, string>;
}

export interface StagingSessionActions {
  /** Save an element change to staging */
  saveElement: (elementId: string, oldValue: string, newValue: string) => Promise<boolean>;
  /** The server's reason the last save was refused, if it gave one. */
  lastSaveError: () => string | null;
  /** Reset dirty count after publish */
  resetDirtyCount: () => void;
  /** Revert all changes — abandon session and start fresh from published version */
  revertAll: () => Promise<boolean>;
  /** Number of dirty elements */
  dirtyCount: number;
}

/** User info needed for staging session */
export interface StagingUser {
  id: string;
  name: string;
}

// =============================================================================
// CONSTANTS
// =============================================================================

/** Heartbeat interval: 5 minutes */
const HEARTBEAT_INTERVAL = 5 * 60 * 1000;

/** Injectable API functions for staging operations */
export interface StagingSessionOptions {
  startSession?: (websiteId: string, userId: string, userName: string) => Promise<{ success: boolean; stagingId?: string; staging?: { id: string }; message?: string }>;
  saveElement?: (websiteId: string, elementId: string, oldValue: string, newValue: string) => Promise<{ success: boolean }>;
  refreshLock?: (websiteId: string) => Promise<{ success: boolean }>;
  releaseLock?: (websiteId: string) => Promise<{ success: boolean }>;
}

// =============================================================================
// HOOK
// =============================================================================

export function useStagingSession(
  websiteId: string | undefined,
  user?: StagingUser | null,
  options?: StagingSessionOptions
): StagingSessionState & StagingSessionActions {
  const [state, setState] = useState<StagingSessionState>({
    isStarting: true,
    sessionId: null,
    error: null,
    dirtyCount: 0,
    hasLock: false,
    pendingChanges: {},
  });

  const heartbeatRef = useRef<ReturnType<typeof setInterval>>();
  const sessionIdRef = useRef<string | null>(null);

  // Resolve injectable API functions (defaults to dashboard imports)
  const startSession = options?.startSession || defaultStartSession;
  const saveElementFn = options?.saveElement || defaultSaveElement;
  const refreshLockFn = options?.refreshLock || defaultRefreshLock;
  const releaseLockFn = options?.releaseLock || defaultReleaseLock;

  // Start or resume staging session
  useEffect(() => {
    if (!websiteId || !user) return;

    let cancelled = false;

    async function initSession() {
      try {
        const result = await startSession(
          websiteId!,
          user!.id,
          user!.name || 'Admin'
        );

        if (cancelled) return;

        if (result.success) {
          const sid = result.stagingId || result.staging?.id;
          sessionIdRef.current = sid || null;
          setState((prev) => ({
            ...prev,
            isStarting: false,
            sessionId: sid || null,
            hasLock: true,
            error: null,
          }));
        } else {
          setState((prev) => ({
            ...prev,
            isStarting: false,
            error: result.message || 'Failed to start editing session',
          }));
        }
      } catch (err) {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          isStarting: false,
          error: err instanceof Error ? err.message : 'Failed to start session',
        }));
      }
    }

    initSession();

    return () => {
      cancelled = true;
    };
  }, [websiteId, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Heartbeat to keep lock alive
  useEffect(() => {
    if (!websiteId || !state.hasLock) return;

    heartbeatRef.current = setInterval(async () => {
      try {
        await refreshLockFn(websiteId);
      } catch (err) {
        console.warn('[Editor] Lock heartbeat failed:', err);
      }
    }, HEARTBEAT_INTERVAL);

    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
      }
    };
  }, [websiteId, state.hasLock]);

  // Release lock on unmount
  useEffect(() => {
    return () => {
      if (websiteId && sessionIdRef.current) {
        // Fire-and-forget release
        releaseLockFn(websiteId).catch(() => {
          // Ignore errors on cleanup
        });
      }
    };
  }, [websiteId]);

  // Save element change. Returns true only when the server accepted it, so the
  // caller can say so when it did not. The portal used to log the failure and
  // carry on, which left an edit on screen that had never been saved.
  const lastSaveErrorRef = useRef<string | null>(null);
  const lastSaveError = useCallback(() => lastSaveErrorRef.current, []);

  const saveElement = useCallback(
    async (elementId: string, oldValue: string, newValue: string): Promise<boolean> => {
      lastSaveErrorRef.current = null;
      if (!websiteId) return false;

      try {
        const result = await saveElementFn(websiteId, elementId, oldValue, newValue);
        if (result && result.success === false) return false;

        setState((prev) => ({
          ...prev,
          dirtyCount: Object.keys(prev.pendingChanges).length + 1,
          pendingChanges: {
            ...prev.pendingChanges,
            [elementId]: newValue,
          },
        }));
        return true;
      } catch (err) {
        console.error('[useStagingSession] Failed to save element:', err);
        // The server explains refusals (text too short, another editor holds the lock).
        lastSaveErrorRef.current = err instanceof Error ? err.message : null;
        return false;
      }
    },
    [websiteId]
  );

  const resetDirtyCount = useCallback(() => {
    setState((prev) => ({
      ...prev,
      dirtyCount: 0,
      pendingChanges: {},
    }));
  }, []);

  // Revert all changes — calls API then resets local state
  const revertAll = useCallback(async (): Promise<boolean> => {
    if (!websiteId) return false;

    try {
      const result = await defaultRevertAll(websiteId);
      if (result.success) {
        const newSessionId = result.stagingId || null;
        sessionIdRef.current = newSessionId;
        setState((prev) => ({
          ...prev,
          sessionId: newSessionId,
          dirtyCount: 0,
          pendingChanges: {},
          error: null,
        }));
        return true;
      }
      return false;
    } catch (err) {
      console.error('[Editor] Failed to revert all changes:', err);
      return false;
    }
  }, [websiteId]);

  return {
    ...state,
    saveElement,
    lastSaveError,
    resetDirtyCount,
    revertAll,
    dirtyCount: Object.keys(state.pendingChanges).length,
  };
}
